import { BoardStatKeys, BoardStats, ConfettiMode, GamePhase, Modal, WarStatus } from '../../app/types';
import { GameEngine } from '../game-engine-helper';
import { SFSObject } from 'sfs2x-api';
import { SFSVAR } from '../sfsVar';
import { bridgeWarGui , bwar_duel, bwar_home } from '../../env';
import {
    claimButton,
    concedeButton,
    continueButton, leaderBoardButton,
    nextHandButton,
    pickBattleButton,
    undoButton,
} from '../buttonsState';
import { closeBWarWindow } from '../../utils/mixed';
import { getCurrentGameEngine } from '../gameEngineRegistry';
import { getEndOfDailyDuelMessage, getEndOfDailyDuelShareFrame, getEndOfWarMessage } from '../../utils/shark-helper';
import { modalGameResults } from '../../utils/modal.helper';
import { sfsService } from '../sfs-service';
import { t } from 'i18next';
import React from 'react';

type EndResultPresentation = {
    confettiMode: ConfettiMode | undefined;
    header: Modal['header'];
    message: string;
};

type BridgeWarEndStage = 'duel' | 'battle' | 'war' | 'daily';

type BridgeWarBattleResult = {
    status: WarStatus;
    duelWins: number;
    duelLosses: number;
};

type BridgeWarEndResult = {
    stage: BridgeWarEndStage;
    duelScore: number;
    duelPlayed: number;
    duelsPerBattle: number;
    battleNumber: number;
    battlesPerWar: number;
    duelsWon: number;
    battleStatus: WarStatus;
    warStatus?: WarStatus;
    resultWin: number;
    resultLoss: number;
    resultDraw: number;
    battleResults: BridgeWarBattleResult[];
    nsTricks: number;
    topResult: number;
};

const getDuelsPerBattle = (duelsPlayed: number): number => {
    try {
        const gameMeta = sfsService.getRoomSFSObject('game_meta');
        const duelsPerBattle = gameMeta.getInt('duels_per_battle');
        if (duelsPerBattle > 0) {
            return duelsPerBattle;
        }
    }
    catch (e) {
        // Fall back to the result data when room metadata is unavailable.
    }

    return Math.max(duelsPlayed, 3);
};

const getBattlesPerWar = (battlesPlayed: number): number => {
    try {
        const gameMeta = sfsService.getRoomSFSObject('game_meta');
        const battlesPerWar = gameMeta.getInt('battles_per_war');
        if (battlesPerWar > 0) {
            return battlesPerWar;
        }
    }
    catch (e) {
        // Fall back to the result data when room metadata is unavailable.
    }

    return Math.max(battlesPlayed, 2);
};

const getEndStageHeader = (context: string, title: string): JSX.Element => React.createElement(
    'div',
    { className: 'bridgeWarEndHeading' },
    React.createElement('div', { className: 'bridgeWarEndContext' }, context),
    React.createElement('div', { className: 'bridgeWarEndTitle' }, title),
);

const getDuelPresentation = (
    duelScore: number,
    duelPlayed: number,
    duelsPerBattle: number,
    battleNumber: number,
    trickMessage: string,
    duelRecord: string,
    battleRecord: string,
): EndResultPresentation => {
    const duelPosition = `Duel ${duelPlayed} of ${duelsPerBattle} (Battle ${battleNumber})`;
    const wonDuel = duelScore > 0;
    const message = wonDuel ? 'To the victor goes the spoils!' : 'But your courage is unshaken';

    return {
        confettiMode: wonDuel ? ConfettiMode.duelWin : ConfettiMode.duelLoss,
        header: getEndStageHeader(duelPosition, wonDuel ? 'Glory is Yours' : 'Bested'),
        message: `<div>${message}</div>
            <div class="bridgeWarTrickMessage">${trickMessage}</div>
            <div>${duelRecord}</div>
            <div class="bridgeWarRecord">${battleRecord}</div>`,
    };
};

const getPracticeDuelPresentation = (
    duelScore: number,
    duelNumber: number,
    totalDuels: number,
    nsTricks: number,
): EndResultPresentation => {
    const wonDuel = duelScore > 0;
    const message = wonDuel ? 'To the victor goes the spoils!' : 'But your courage is unshaken';
    const trickMessage = `You took ${nsTricks} ${nsTricks === 1 ? 'trick' : 'tricks'}`;

    return {
        confettiMode: wonDuel ? ConfettiMode.duelWin : ConfettiMode.duelLoss,
        header: getEndStageHeader(`Duel ${duelNumber} of ${totalDuels}`, wonDuel ? 'Glory is Yours' : 'Bested'),
        message: `<div>${message}</div><div class="bridgeWarTrickMessage">${trickMessage}</div>`,
    };
};

const getBattlePresentation = (
    battleStatus: WarStatus,
    isDailyDuel: boolean,
    battleNumber: number,
    battlesPerWar: number,
    duelRecord: string,
    battleRecord: string,
): EndResultPresentation => {
    let confettiMode: ConfettiMode | undefined;

    switch (battleStatus) {
        case WarStatus.topWin:
            confettiMode = ConfettiMode.battleBigWin;
            break;
        case WarStatus.goodWin:
        case WarStatus.win:
            confettiMode = ConfettiMode.battleSmallWin;
            break;
        case WarStatus.loss:
            confettiMode = ConfettiMode.battleLoss;
            break;
    }

    return {
        confettiMode,
        header: getEndStageHeader(
            `Battle ${battleNumber} of ${battlesPerWar}`,
            t(`battleEndHeaders.${battleStatus}.${isDailyDuel}`),
        ),
        message: `<div class="bridgeWarBattleMessage">${t(`warEndBlurb.${battleStatus}.${isDailyDuel}`)}</div>
            <div>${duelRecord}</div>
            <div class="bridgeWarRecord">${battleRecord}</div>`,
    };
};

const getBattleStatusLabel = (status: WarStatus): string => {
    switch (status) {
        case WarStatus.topWin:
        case WarStatus.goodWin:
        case WarStatus.win:
            return 'Win';
        case WarStatus.loss:
            return 'Loss';
        case WarStatus.draw:
            return 'Draw';
        default:
            return 'In Progress';
    }
};

const getBattleResultsTable = (battleResults: BridgeWarBattleResult[]): string => {
    const headings = battleResults.map((_, index) => `<th scope="col">Battle ${index + 1}</th>`).join('');
    const statuses = battleResults
        .map((battle) => `<td>${getBattleStatusLabel(battle.status)}</td>`)
        .join('');
    const duelRecords = battleResults
        .map((battle) => `<td>${battle.duelWins} - ${battle.duelLosses}</td>`)
        .join('');

    return `<div class="bridgeWarResultsTableWrapper">
        <table class="bridgeWarResultsTable">
            <thead><tr>${headings}</tr></thead>
            <tbody>
                <tr aria-label="Battle results">${statuses}</tr>
                <tr aria-label="Duel records">${duelRecords}</tr>
            </tbody>
        </table>
    </div>`;
};

const getWarPresentation = (
    warStatus: WarStatus,
    isDailyDuel: boolean,
    battleRecord: string,
    battleResultsTable: string,
): EndResultPresentation => {
    let confettiMode: ConfettiMode | undefined;

    switch (warStatus) {
        case WarStatus.topWin:
            confettiMode = ConfettiMode.warBigWin;
            break;
        case WarStatus.goodWin:
            confettiMode = ConfettiMode.warSmallWin;
            break;
        case WarStatus.win:
            confettiMode = ConfettiMode.battleSmallWin;
            break;
        case WarStatus.loss:
            confettiMode = ConfettiMode.warLoss;
            break;
        case WarStatus.draw:
            confettiMode = ConfettiMode.warDraw;
            break;
    }

    return {
        confettiMode,
        header: React.createElement(
            'div',
            { className: 'bridgeWarWarTitle' },
            t(`warEndHeaders.${warStatus}.${isDailyDuel}`),
        ),
        message: `<div class="bridgeWarWarMessage">${t(`warEndBlurb.${warStatus}.${isDailyDuel}`)}</div>
            <div>${battleRecord}</div>
            ${battleResultsTable}`,
    };
};

const showAfterCurrentModalCloses = (showModal: () => void) => {
    setTimeout(showModal, 0);
};

const showEndResultModal = (
    sender: GameEngine,
    presentation: EndResultPresentation,
    okLabel: string,
    confirm: () => void,
) => {
    sender.props.app_addModal(
        getEndOfWarMessage(
            presentation.confettiMode,
            presentation.message,
            presentation.header,
            {
                cancelLabel: 'Home Base',
                okLabel,
                cancel: () => {
                    parent.postMessage('reloaddash', '*');
                    parent.location.href = sender.returnURL;
                },
                confirm,
            },
        ),
    );
};

const showBridgeWarEndResult = (sender: GameEngine, result: BridgeWarEndResult) => {
    const isDailyDuel = result.stage === 'daily';
    const duelRecord = `Duel Record </br> ${result.duelsWon} - ${result.duelPlayed - result.duelsWon}`;
    const trickMessage = `You took ${result.nsTricks} ${result.nsTricks === 1 ? 'trick' : 'tricks'}`;
    const battleRecord = `Battle Record </br> ${result.resultWin} - ${result.resultLoss} - ${result.resultDraw}`;
    const battleResultsTable = getBattleResultsTable(result.battleResults);
    const duelPresentation = getDuelPresentation(
        result.duelScore,
        result.duelPlayed,
        result.duelsPerBattle,
        result.battleNumber,
        trickMessage,
        duelRecord,
        battleRecord,
    );

    const showDailyDuelResult = () => {
        const warStatus = result.warStatus ?? result.battleStatus;
        const warPresentation = getWarPresentation(
            warStatus,
            true,
            battleRecord,
            battleResultsTable,
        );

        sender.props.app_addModal(
            getEndOfDailyDuelMessage(
                warPresentation.confettiMode,
                `${t(`warEndBlurb.${warStatus}.true`)}
                    </br>
                    ${trickMessage}
                    </br>
                    ${t(`warLegendBlurb.${warStatus}.true`, { topResult: result.topResult })}`,
                warPresentation.header,
                {
                    cancelLabel: 'Keep Playing Now',
                    okLabel: 'Share',
                    cancel: () => {
                        parent.location.href = bwar_home;
                    },
                    confirm: () => {
                        sender.props.app_removeModal();
                        sender.props.app_addModal(getEndOfDailyDuelShareFrame());
                    },
                },
            ),
        );
    };

    const showWarResult = () => {
        const warPresentation = getWarPresentation(
            result.warStatus ?? result.battleStatus,
            false,
            battleRecord,
            battleResultsTable,
        );

        showEndResultModal(sender, warPresentation, 'War Review', () => {
            const _modalGameResults = modalGameResults({
                gameResults: sender.props.table.sharkGameResultsV2,
                noCancel: false,
                noClickOutside: true,
                fullSize: true,
            });
            sender.props.app_addModal(_modalGameResults);
        });
    };

    const showBattleResult = (showWarAfterBattle: boolean) => {
        const battlePresentation = getBattlePresentation(
            result.battleStatus,
            false,
            result.battleNumber,
            result.battlesPerWar,
            duelRecord,
            battleRecord,
        );

        showEndResultModal(
            sender,
            battlePresentation,
            showWarAfterBattle ? 'OK' : 'Next Battle',
            showWarAfterBattle
                ? () => showAfterCurrentModalCloses(showWarResult)
                : () => sender.clientRequestsNextHand(),
        );
    };

    if (result.stage === 'daily') {
        sender.props.table_updateFooterButtons([{ key: 'button-4', button: pickBattleButton }]);
        showDailyDuelResult();
        return;
    }

    if (result.stage === 'war') {
        sender.props.table_updateFooterButtons([{ key: 'button-4', button: pickBattleButton }]);
        showEndResultModal(sender, duelPresentation, 'OK', () => {
            showAfterCurrentModalCloses(() => showBattleResult(true));
        });
        return;
    }

    if (result.stage === 'battle') {
        showEndResultModal(sender, duelPresentation, 'OK', () => {
            showAfterCurrentModalCloses(() => showBattleResult(false));
        });
        return;
    }

    showEndResultModal(sender, duelPresentation, 'Next Duel', () => {
        sender.clientRequestsNextHand();
    });
};

const getSimulatedBridgeWarEndResult = (stage: BridgeWarEndStage): BridgeWarEndResult => ({
    stage,
    duelScore: 120,
    duelPlayed: stage === 'duel' ? 2 : stage === 'daily' ? 1 : 3,
    duelsPerBattle: stage === 'daily' ? 1 : 3,
    battleNumber: stage === 'daily' ? 1 : 2,
    battlesPerWar: stage === 'daily' ? 1 : 2,
    duelsWon: stage === 'duel' ? 1 : stage === 'daily' ? 1 : 2,
    battleStatus: WarStatus.win,
    warStatus: stage === 'war' ? WarStatus.topWin : stage === 'daily' ? WarStatus.win : undefined,
    resultWin: stage === 'duel' ? 0 : stage === 'daily' ? 1 : 2,
    resultLoss: stage === 'duel' ? 1 : 0,
    resultDraw: 0,
    battleResults: stage === 'daily'
        ? [{ status: WarStatus.win, duelWins: 1, duelLosses: 0 }]
        : [
              { status: WarStatus.win, duelWins: 2, duelLosses: 1 },
              { status: WarStatus.win, duelWins: 3, duelLosses: 0 },
          ],
    nsTricks: 9,
    topResult: 250,
});

const initializeBridgeWarEndSimulation = () => {
    if (process.env.NODE_ENV !== 'development' || !bridgeWarGui) {
        return;
    }

    const stage = new URLSearchParams(window.location.search).get('simulateBWarEnd') as BridgeWarEndStage | null;
    if (!stage || !['duel', 'battle', 'war', 'daily'].includes(stage)) {
        return;
    }

    let attempts = 0;
    const showWhenEngineIsReady = () => {
        const sender = getCurrentGameEngine();
        if (sender) {
            showBridgeWarEndResult(sender, getSimulatedBridgeWarEndResult(stage));
            return;
        }

        attempts += 1;
        if (attempts < 50) {
            setTimeout(showWhenEngineIsReady, 100);
        }
    };

    setTimeout(showWhenEngineIsReady, 0);
};

initializeBridgeWarEndSimulation();

export function MID_TIME_TO_SCORE(cmdObj: SFSObject, sender: GameEngine) {
    return;
}

export async function MID_TIME_TO_SCORE_SB(cmdObj: SFSObject, sender: GameEngine) {
    sender.hideButtons([claimButton, concedeButton, undoButton]);
    sender.props.table_setAuxiliaryMessage({ position: 'center', message: undefined });
    sender.props.table_setAuxiliaryMessage({ position: 'right', message: undefined });
    sender.props.table_setAuxiliaryMessage({ position: 'left', message: undefined });
    sender.props.table_setGamePhase(GamePhase.END);

    const oblState: SFSObject | undefined = cmdObj.containsKey('oplay') ? cmdObj.getSFSObject('oplay') : undefined;
    if (oblState) {
        sender.setHandsFromScore(oblState, true, 'h');
    }
    const nsTricks: number = cmdObj.containsKey('nstr')
        ? cmdObj.getInt('nstr')
        : oblState?.containsKey('nstr')
        ? oblState.getInt('nstr')
        : 0;
    const ewTricks: number = cmdObj.containsKey('ewtr')
        ? cmdObj.getInt('ewtr')
        : oblState?.containsKey('ewtr')
        ? oblState.getInt('ewtr')
        : 0;

    sender.props.table_setTrickCounts({ ns: nsTricks, ew: ewTricks });
    const score: number = cmdObj.getInt(SFSVAR.SFSGAME_MID_LOAD);


    if (!sender.props.app.engineStateInterface.isMiniBridge) {
        sender.props.table_setShowAuctionLine(true);
    }
    //
    try {
        if (cmdObj.containsKey('stats')) {
            const json = new TextDecoder('utf-8').decode(cmdObj.getByteArray('stats'));
            const boardStats: BoardStats[] = JSON.parse(json) as BoardStats[];
            sender.props.table_setBoardStats(boardStats);
            if (cmdObj.containsKey('mystat')) {
                const myStatsKey = JSON.parse(
                    new TextDecoder('utf-8').decode(cmdObj.getByteArray('mystat')),
                ) as BoardStatKeys;
                if (myStatsKey) {
                    sender.props.table_setMyBoardStatKeys(myStatsKey);
                }
            } else {
                sender.props.table_setMyBoardStatKeys({} as BoardStatKeys);
            }
        } else {
            sender.props.table_setBoardStats([]);
            sender.props.table_setMyBoardStatKeys({} as BoardStatKeys);
        }
    } catch (e) {
        //
    }

    try {
        if (cmdObj.containsKey('board')) {
            const json = new TextDecoder('utf-8').decode(cmdObj.getByteArray('board'));
            const boardStats: BoardStats[] = JSON.parse(json) as BoardStats[];
           // sender.props.table_setBoardStats(boardStats);

        } else {
            sender.props.table_setBoardStats([]);
            sender.props.table_setMyBoardStatKeys({} as BoardStatKeys);
        }
    } catch (e) {
        //
    }

    const delay = () => new Promise((resolve) => setTimeout(resolve, 0));
    await delay();

    sender.updateBoardData(undefined);

    if (sender.props.app.engineStateInterface.isInstaGame) {
        sender.props.table_setWalkThroughShowComponents(['sharkInstaBoardReviewComponent'])
        sender.props.table_setWalkTrough({ content: `` });
    }else {
        sender.props.table_setWalkThroughShowComponents(['sharkBoardReviewComponent'])
        sender.props.table_setWalkTrough({ content: `Your Score: ${score}` });
    }

    if (sender.props.app.engineStateInterface.hasNext) {
        sender.showButtons([nextHandButton]);
    }
    else {
        sender.showButtons([leaderBoardButton]);
    }
}

export async function MID_TIME_TO_SCORE_BWAR(cmdObj: SFSObject, sender: GameEngine) {
    const delay = () => new Promise((resolve) => setTimeout(resolve, 0));
    await delay();
    sender.hideButtons([claimButton, concedeButton, undoButton]);
    sender.props.table_setAuxiliaryMessage({ position: 'center', message: undefined });
    sender.props.table_setAuxiliaryMessage({ position: 'right', message: undefined });
    sender.props.table_setAuxiliaryMessage({ position: 'left', message: undefined });
    sender.props.table_setGamePhase(GamePhase.END);
    sender.props.table_setShowAuctionLine(false);
    const oblState: SFSObject | undefined = cmdObj.containsKey('oplay') ? cmdObj.getSFSObject('oplay') : undefined;
    if (oblState) {
        sender.setHandsFromScore(oblState, true, 'h');
    }
    const nsTricks: number = cmdObj.containsKey('nstr') ? cmdObj.getInt('nstr') : oblState?.containsKey('nstr') ? oblState.getInt('nstr') : 0;
    const ewTricks: number = cmdObj.containsKey('ewtr') ? cmdObj.getInt('ewtr') : oblState?.containsKey('ewtr') ? oblState.getInt('ewtr') : 0;

    sender.props.table_setTrickCounts({ ns: nsTricks, ew: ewTricks });
    //sender.props.table_setWalkTrough({ content: `You took ${nsTricks} ${nsTricks === 1 ? 'trick' : 'tricks'}` });

    try {
        if (cmdObj.containsKey('stats')) {
            const json = new TextDecoder('utf-8').decode(cmdObj.getByteArray('stats'));
            const boardStats: BoardStats[] = JSON.parse(json) as BoardStats[];
            sender.props.table_setBoardStats(boardStats);
            if (cmdObj.containsKey('mystat')) {
                const myStatsKey: BoardStatKeys = JSON.parse(
                    new TextDecoder('utf-8').decode(cmdObj.getByteArray('mystat')),
                ) as BoardStatKeys;
                sender.props.table_setMyBoardStatKeys(myStatsKey);
            } else {
                sender.props.table_setMyBoardStatKeys({} as BoardStatKeys);
            }
        } else {
            sender.props.table_setBoardStats([]);
            sender.props.table_setMyBoardStatKeys({} as BoardStatKeys);
        }
    }
    catch (e) {
        //
    }

    if (sender.props.app.engineStateInterface.hasNext) {
        sender.showButtons([nextHandButton]);
    } else {
        sender.showButtons([continueButton]);
    }

    sender.updateBoardData(undefined);

    if (cmdObj.containsKey('practice')) {
        const duelScore = cmdObj.containsKey(SFSVAR.SFSGAME_MID_LOAD)
            ? cmdObj.getInt(SFSVAR.SFSGAME_MID_LOAD)
            : 0;
        const dealIndex = cmdObj.containsKey(SFSVAR.SFSGAME_DEAL_SET_START_INDEX)
            ? cmdObj.getInt(SFSVAR.SFSGAME_DEAL_SET_START_INDEX)
            : 0;
        const duelNumber = dealIndex + 1;
        const totalDuels = sfsService.getNumber(
            SFSVAR.SFSGAME_ROOM_NB,
            sfsService.getLobbyNumber(SFSVAR.SFSGAME_ROOM_NB, duelNumber),
        ) ?? duelNumber;
        const hasNext = duelNumber < totalDuels;
        const presentation = getPracticeDuelPresentation(duelScore, duelNumber, totalDuels, nsTricks);

        showEndResultModal(sender, presentation, hasNext ? 'Next Duel' : 'Finish', () => {
            if (hasNext) {
                sender.clientRequestsNextHand();
            } else {
                closeBWarWindow(sender.returnURL);
            }
        });
        return;
    }

    const myresults = cmdObj.getSFSObject('myresults');
    if (!myresults) {
        return;
    }

    const war_record = myresults.getSFSArray('war_record');
    const topResult = war_record.getSFSObject(3).getInt('pts');
    const war_status: WarStatus | undefined = myresults.containsKey('war_status') ? myresults.getUtfString('war_status') as WarStatus : undefined;
    const isEndOfWar = war_status !== WarStatus.progress && war_status !== undefined;

    const battles = myresults.getSFSArray('battles');
    const currentBattle = battles.getSFSObject(battles.size() - 1);
    const battleStatus = currentBattle.getUtfString('status');
    const endOfBattle = battleStatus !== WarStatus.progress && battleStatus !== undefined;

    const duels = currentBattle.getSFSArray('duels');
    const lastDuel = duels.getSFSObject(duels.size() - 1);
    const duelPlayed = duels.size();
    const duelsWon = currentBattle.getInt('wins');
    const isDailyDuel = battles.size() === 1;
    const battleResults: BridgeWarBattleResult[] = [...Array(battles.size()).keys()].map((index) => {
        const battle = battles.getSFSObject(index);
        const battleDuels = battle.getSFSArray('duels');
        const battleDuelWins = battle.getInt('wins');

        return {
            status: battle.getUtfString('status') as WarStatus,
            duelWins: battleDuelWins,
            duelLosses: battleDuels.size() - battleDuelWins,
        };
    });

    showBridgeWarEndResult(sender, {
        stage: isEndOfWar ? (isDailyDuel ? 'daily' : 'war') : endOfBattle ? 'battle' : 'duel',
        duelScore: lastDuel.getInt('s'),
        duelPlayed,
        duelsPerBattle: getDuelsPerBattle(duelPlayed),
        battleNumber: battles.size(),
        battlesPerWar: getBattlesPerWar(battles.size()),
        duelsWon,
        battleStatus: battleStatus as WarStatus,
        warStatus: war_status,
        resultWin: war_record ? war_record.getInt(0) : 0,
        resultLoss: war_record ? war_record.getInt(1) : 0,
        resultDraw: war_record ? war_record.getInt(2) : 0,
        battleResults,
        nsTricks,
        topResult,
    });
    return;
}
